import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import FormInput from "../inputs/FormInput";
import TextEditor from "../inputs/TextEditor";
import LanguageToggle from "../global/LanguageToggle";

const FormContactSection = ({ currentData, onSubmit, isLoading }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const [bannerTitleAr, setBannerTitleAr] = useState("");
    const [bannerTitleEn, setBannerTitleEn] = useState("");
    const [formTitleAr, setFormTitleAr] = useState("");
    const [formTitleEn, setFormTitleEn] = useState("");
    const [formSubTitleAr, setFormSubTitleAr] = useState("");
    const [formSubTitleEn, setFormSubTitleEn] = useState("");
    const [formDescriptionAr, setFormDescriptionAr] = useState("");
    const [formDescriptionEn, setFormDescriptionEn] = useState("");
    const [locationTitleAr, setLocationTitleAr] = useState("");
    const [locationTitleEn, setLocationTitleEn] = useState("");
    const [locationSubTitleAr, setLocationSubTitleAr] = useState("");
    const [locationSubTitleEn, setLocationSubTitleEn] = useState("");
    const [bannerImage, setBannerImage] = useState(null);
    const [mapIframe, setMapIframe] = useState("");

    useEffect(() => {
        if (currentData) {
            setBannerTitleAr(currentData?.banner_title?.ar || "");
            setBannerTitleEn(currentData?.banner_title?.en || "");
            setFormTitleAr(currentData?.form_title?.ar || "");
            setFormTitleEn(currentData?.form_title?.en || "");
            setFormSubTitleAr(currentData?.form_sub_title?.ar || "");
            setFormSubTitleEn(currentData?.form_sub_title?.en || "");
            setFormDescriptionAr(currentData?.form_description?.ar || "");
            setFormDescriptionEn(currentData?.form_description?.en || "");
            setLocationTitleAr(currentData?.location_title?.ar || "");
            setLocationTitleEn(currentData?.location_title?.en || "");
            setLocationSubTitleAr(currentData?.location_sub_title?.ar || "");
            setLocationSubTitleEn(currentData?.location_sub_title?.en || "");
            setBannerImage(currentData?.banner_image || null);
            setMapIframe(currentData?.map_iframe || "");
        }
    }, [currentData]);

    const getImageName = (imageVal) => {
        if (!imageVal) return "";
        if (typeof imageVal === 'string') {
            return imageVal.substr(imageVal.lastIndexOf('/') + 1);
        }
        return imageVal;
    };

    const handleBannerImageChange = (e) => {
        setBannerImage(e.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            banner_title: {
                ar: bannerTitleAr || bannerTitleEn,
                en: bannerTitleEn || bannerTitleAr,
            },
            form_title: {
                ar: formTitleAr || formTitleEn,
                en: formTitleEn || formTitleAr,
            },
            form_sub_title: {
                ar: formSubTitleAr || formSubTitleEn,
                en: formSubTitleEn || formSubTitleAr,
            },
            form_description: {
                ar: formDescriptionAr || formDescriptionEn,
                en: formDescriptionEn || formDescriptionAr,
            },
            location_title: {
                ar: locationTitleAr || locationTitleEn,
                en: locationTitleEn || locationTitleAr,
            },
            location_sub_title: {
                ar: locationSubTitleAr || locationSubTitleEn,
                en: locationSubTitleEn || locationSubTitleAr,
            },
            banner_image: getImageName(bannerImage),
            map_iframe: mapIframe,
        };

        onSubmit(payload);
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            {/* ===== Banner Section ===== */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isArabic ? "صورة البانر" : "Banner"}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <FormInput
                            type="file"
                            name="banner_image"
                            labelImage={isArabic ? "صورة البانر" : "Banner Image"}
                            value={bannerImage}
                            onChange={handleBannerImageChange}
                            width={200}
                            height={120}
                        />
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="عنوان البانر (عربي)"
                                placeholder="أدخل عنوان البانر بالعربي"
                                value={bannerTitleAr}
                                onChange={(e) => setBannerTitleAr(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Banner Title (English)"
                                placeholder="Enter banner title in English"
                                value={bannerTitleEn}
                                onChange={(e) => setBannerTitleEn(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* ===== Form Section ===== */}
            <Paper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {isArabic ? "قسم النموذج" : "Form Section"}
                </Typography>

                <Grid container spacing={3}>
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="عنوان النموذج (عربي)"
                                placeholder="أدخل عنوان النموذج بالعربي"
                                value={formTitleAr}
                                onChange={(e) => setFormTitleAr(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Form Title (English)"
                                placeholder="Enter form title in English"
                                value={formTitleEn}
                                onChange={(e) => setFormTitleEn(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الفرعي للنموذج (عربي)"
                                placeholder="أدخل العنوان الفرعي للنموذج بالعربي"
                                value={formSubTitleAr}
                                onChange={(e) => setFormSubTitleAr(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Form Sub Title (English)"
                                placeholder="Enter form sub title in English"
                                value={formSubTitleEn}
                                onChange={(e) => setFormSubTitleEn(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                وصف النموذج (عربي)
                            </Typography>
                            <TextEditor
                                name="form_description_ar"
                                value={formDescriptionAr}
                                onChange={(e) => setFormDescriptionAr(e.target.value)}
                                placeholder="أدخل وصف النموذج بالعربي"
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Form Description (English)
                            </Typography>
                            <TextEditor
                                name="form_description_en"
                                value={formDescriptionEn}
                                onChange={(e) => setFormDescriptionEn(e.target.value)}
                                placeholder="Enter form description in English"
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* ===== Location Section ===== */}
            <Paper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {isArabic ? "قسم الموقع" : "Location Section"}
                </Typography>

                <Grid container spacing={3}>
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="عنوان الموقع (عربي)"
                                placeholder="أدخل عنوان الموقع بالعربي"
                                value={locationTitleAr}
                                onChange={(e) => setLocationTitleAr(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Location Title (English)"
                                placeholder="Enter location title in English"
                                value={locationTitleEn}
                                onChange={(e) => setLocationTitleEn(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الفرعي للموقع (عربي)"
                                placeholder="أدخل العنوان الفرعي للموقع بالعربي"
                                value={locationSubTitleAr}
                                onChange={(e) => setLocationSubTitleAr(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Location Sub Title (English)"
                                placeholder="Enter location sub title in English"
                                value={locationSubTitleEn}
                                onChange={(e) => setLocationSubTitleEn(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Map Iframe */}
                    <Grid item xs={12}>
                        <FormInput
                            type="textarea"
                            label={isArabic ? "كود الخريطة (Iframe)" : "Map Iframe"}
                            placeholder={isArabic ? '<iframe src="..."></iframe>' : '<iframe src="..."></iframe>'}
                            value={mapIframe}
                            onChange={(e) => setMapIframe(e.target.value)}
                            textareaRows="120px"
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{ minWidth: 120 }}
                >
                    {isLoading ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                </Button>
            </Box>
        </Box>
    );
};

export default FormContactSection;
